// Eskom PARCC — curated finding themes (Lindiwe feedback Jun 23 2026, item 1b).
//
// The dashboard's "Recurring Findings" chart groups findings into a small set of
// human-meaningful THEMES ("common themes such as tax or statutory-document
// issues" — Lindiwe's words). The rulebook's own rule labels are verbose,
// route-specific sentences (e.g. "Award not aligned to DAA / highest scorer /
// validity / tax"), not tidy categories — so we DERIVE a theme from a finding by
// keyword-matching the rule labels it matched (`matchedRules`), falling back to the
// reviewer's note text when a finding has no matched rule (analyst-captured lines).
//
// A finding may exhibit MORE THAN ONE theme (the example above is DAA + Validity +
// Tax); themeFor returns every theme it matches, so the chart shows each common
// theme it recurs under. A finding matching nothing curated falls to 'Other'.
//
// Pure + data-only so it unit-tests in isolation; the chart wiring lives in the
// dashboard. The taxonomy is intentionally editable here in one place.

export const FINDING_THEMES = [
  'Tax',
  'Validity',
  'CSD / Registration',
  'Statutory documents',
  'DAA / Award',
  'Conflict of interest',
  'Thresholds / Route',
  'EDMS / Filing',
  'BBBEE / Preference',
  'Housekeeping / Admin',
  'Other',
] as const;
export type FindingTheme = (typeof FINDING_THEMES)[number];

/** The theme that swallows anything not matched by a curated pattern. */
export const OTHER_THEME: FindingTheme = 'Other';

/**
 * Curated keyword patterns, one (or more) per theme. Matched case-insensitively
 * against a finding's rule labels (or note text). Ordered for stable output; a
 * finding can match several. 'Other' has no pattern — it is the fallback.
 */
const THEME_PATTERNS: ReadonlyArray<readonly [FindingTheme, RegExp]> = [
  ['Tax', /\btax(es|ation)?\b|tax clearance|\bsars\b/i],
  ['Validity', /\bvalidit\w*\b|valid(?:ity)? period|\bexpired\b|\blapsed\b/i],
  ['CSD / Registration', /\bcsd\b|central supplier database|supplier registration|\bregistrat\w*\b/i],
  ['Statutory documents', /\bstatutory\b|\breturnable\w*\b|company registration|\bcipc\b|certificate of/i],
  ['DAA / Award', /\bdaa\b|delegation of authority|\baward\b|highest scorer|\badjudicat\w*\b/i],
  ['Conflict of interest', /\bcoi\b|conflict of interest|\bnda\b|declaration of interest/i],
  ['Thresholds / Route', /\bthreshold\b|wrong route|route used|\bceiling\b|exceeds?\b/i],
  ['EDMS / Filing', /\bedms\b|\bfiling\b|\bfiled?\b|records management|document management/i],
  ['BBBEE / Preference', /b-?bbee|broad-based|\bpreferential\b|\bpreference\b|\bpppfa\b/i],
  ['Housekeeping / Admin', /\bhousekeeping\b|\badministrative\b|\badmin\b/i],
];

/** The minimal finding shape the theme mapper reads (structural, not the full row). */
export interface ThemeInput {
  matchedRules?: readonly string[] | null;
  text?: string | null;
}

/**
 * The curated theme(s) a finding exhibits. Keyword-matched against the finding's
 * matched rule labels; when it has none (an analyst-captured line), the reviewer's
 * note text is used instead. Returns every matched theme, or `['Other']` when none
 * of the curated patterns hit. Always returns at least one theme.
 */
export function themeFor(finding: ThemeInput): FindingTheme[] {
  const rules = (finding.matchedRules ?? []).filter((r): r is string => typeof r === 'string' && r.length > 0);
  const haystack = (rules.length > 0 ? rules.join(' ') : (finding.text ?? '')).toLowerCase();
  if (!haystack.trim()) return [OTHER_THEME];

  const themes: FindingTheme[] = [];
  for (const [theme, pattern] of THEME_PATTERNS) {
    if (pattern.test(haystack)) themes.push(theme);
  }
  return themes.length > 0 ? themes : [OTHER_THEME];
}
